import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  Check,
  ChevronRight,
  ChevronLeft,
  User,
  Stethoscope,
  Users,
  Building,
  Award,
  CheckCircle,
  Loader2,
  LogOut,
} from "lucide-react";
import logo from "../assets/logo.svg";
import axios from "../config/axios";
import { authService } from "../services/authService";

// Define types for the form data
interface OnboardingFormData {
  name: string;
  specialty: string;
  role: string;
  practice_size: string;
}

// API response type
interface ApiResponse {
  success: boolean;
  message?: string;
  data?: any;
}

// Button component
interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "gradient";
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  disabled = false,
  variant = "primary",
  className = "",
}) => {
  const baseClasses =
    "px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-200";

  const variantClasses = {
    primary: "bg-blue-600 text-white hover:bg-blue-700",
    secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    gradient:
      "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-600/20",
  };

  const disabledClasses = disabled ? "opacity-60 cursor-not-allowed" : "";

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant]} ${disabledClasses} ${className}`}
    >
      {children}
    </button>
  );
};

const DoctorOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<OnboardingFormData>({
    name: "",
    specialty: "",
    role: "",
    practice_size: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiResponse, setApiResponse] = useState<ApiResponse | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  // Define the steps for the onboarding process
  const steps = [
    { name: "Welcome", icon: <Award size={20} /> },
    { name: "Name", icon: <User size={20} /> },
    { name: "Specialty", icon: <Stethoscope size={20} /> },
    { name: "Role", icon: <Award size={20} /> },
    { name: "Practice", icon: <Building size={20} /> },
    { name: "Complete", icon: <Check size={20} /> },
  ];

  // Specialty options
  const specialties = [
    "Family Medicine",
    "Internal Medicine",
    "Pediatrics",
    "Psychiatry",
    "Mental Health Counseling",
    "Cardiology",
    "Dermatology",
    "Endocrinology",
    "Gastroenterology",
    "Neurology",
    "Obstetrics & Gynecology",
    "Oncology",
    "Orthopedics",
    "Pulmonology",
    "Rheumatology",
    "Surgery",
    "Urology",
    "Other",
  ];

  // Role options
  const roles = [
    "Physician",
    "Nurse Practitioner",
    "Physician Assistant",
    "Registered Nurse",
    "Medical Assistant",
    "Other",
  ];

  // Practice size options
  const practiceSizes = [
    "Just me (solo practice)",
    "2 to 9",
    "10 to 49",
    "50 or more",
  ];

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle next step
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Final step - directly submit to API instead of showing JSON
      handleSubmitToBackend();
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Check if the current step is valid to proceed
  const isStepValid = () => {
    switch (currentStep) {
      case 0: // Welcome step
        return true;
      case 1: // Name step
        return formData.name.trim() !== "";
      case 2: // Specialty step
        return formData.specialty !== "";
      case 3: // Role step
        return formData.role !== "";
      case 4: // Practice step
        return formData.practice_size !== "";
      default:
        return true;
    }
  };

  // Handle final submission to backend API
  const handleSubmitToBackend = async () => {
    setIsSubmitting(true);
    setApiError(null);
    setApiResponse(null);

    try {
      // Make API call using axios directly, matching the pattern in Settings.tsx
      const response = await axios.post("/api/scribing/user-profile/onboard", formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200 || response.status === 201) {
        // Mark onboarding as complete in localStorage after successful API call
        localStorage.setItem("onboarding_complete", "true");
        console.log("DoctorOnboarding - Onboarding marked as complete in localStorage");
        
        setApiResponse({
          success: true,
          data: response.data,
        });

        setTimeout(() => {
          console.log("Onboarding complete, directly navigating to mynotes page");
          window.location.href = "/mynotes";
        }, 1500);
      } else {
        setApiResponse({
          success: false,
          message: response.data?.message || "An error occurred",
        });
        setApiError(response.data?.message || "Failed to save profile data");
      }
    } catch (error) {
      console.error("API call error:", error);
      console.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers,
        }
      });
      
      const errorMessage =
        error.response?.data?.message || "Network error or server unavailable";
      setApiError(errorMessage);
      setApiResponse({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="w-full bg-white shadow-sm py-4">
        <div className="flex justify-between items-center px-6">
          <Link to="/" className="flex items-center">
            <img src={logo} alt="DocuMD" className="h-10 w-auto" />
          </Link>
          
          <button 
            onClick={() => authService.logout()}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </button>
        </div>
      </div>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col">
        {/* Header with logo */}

        {/* Progress bar */}
        <div className="w-full bg-white shadow-sm py-8 px-6 border-b border-gray-100">
          <div className="max-w-4xl mx-auto">
            {/* Step numbers above the progress bar */}
            <div className="flex items-center justify-between mb-3 px-2">
              {steps.map((step, index) => (
                <div
                  key={`step-number-${index}`}
                  className={`text-xs font-semibold ${
                    index <= currentStep ? "text-blue-600" : "text-gray-400"
                  }`}
                >
                  Step {index + 1}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mb-2 relative">
              {/* Progress bar container */}
              <div className="absolute top-5 left-5 right-5 h-2 bg-gray-200 rounded-full -z-10"></div>

              {/* Active progress bar */}
              <div
                className="absolute top-5 left-5 h-2 bg-blue-600 rounded-full -z-5 transition-all duration-500 ease-in-out"
                style={{
                  width:
                    currentStep === 0
                      ? "0%"
                      : currentStep === steps.length - 1
                      ? "calc(100% - 10px)"
                      : `calc(${(currentStep / (steps.length - 1)) * 100}%)`,
                }}
              />

              {steps.map((step, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center relative z-10"
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 border-white shadow-md ${
                      index < currentStep
                        ? "bg-blue-600 text-white"
                        : index === currentStep
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {index < currentStep ? (
                      <Check size={18} />
                    ) : (
                      <span className="flex items-center justify-center">
                        {step.icon}
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-xs mt-2 font-medium ${
                      index <= currentStep ? "text-blue-700" : "text-gray-500"
                    }`}
                  >
                    {step.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white rounded-xl shadow-xl p-10 w-full max-w-2xl border border-gray-100">
            {/* Welcome Step */}
            {currentStep === 0 && (
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-8">
                  <Award size={40} className="text-blue-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Let's Get Started
                </h2>
                <p className="text-lg text-gray-700 mb-8">
                  We'll guide you through a few quick steps to set up your
                  profile. This will help us customize your experience and make
                  your notes more efficient.
                </p>
                <p className="text-lg text-gray-700 mb-8">
                  Ready? Press "Next" and let's create your first note together.
                  Thank you for being a clinician.
                </p>
              </div>
            )}

            {/* Name Step */}
            {currentStep === 1 && (
              <div>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <User size={32} className="text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Sign notes with your name
                </h2>
                <div className="mb-6">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                    placeholder="Dr. Jane Smith"
                  />
                </div>
              </div>
            )}

            {/* Specialty Step */}
            {currentStep === 2 && (
              <div>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Stethoscope size={32} className="text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Tailor your notes to your specialty
                </h2>
                <div className="mb-6">
                  <label
                    htmlFor="specialty"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Your Specialty
                  </label>
                  <select
                    id="specialty"
                    name="specialty"
                    value={formData.specialty}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    <option value="" disabled>
                      Select your specialty
                    </option>
                    {specialties.map((specialty) => (
                      <option key={specialty} value={specialty}>
                        {specialty}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Role Step */}
            {currentStep === 3 && (
              <div>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award size={32} className="text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  What best describes your role?
                </h2>
                <div className="mb-6">
                  <label
                    htmlFor="role"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Your Role
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    <option value="" disabled>
                      Select your role
                    </option>
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Practice Size Step */}
            {currentStep === 4 && (
              <div>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Building size={32} className="text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  How many clinicians work in your practice or department?
                </h2>
                <div className="mb-6">
                  <label
                    htmlFor="practiceSize"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Practice Size
                  </label>
                  <select
                    id="practice_size"
                    name="practice_size"
                    value={formData.practice_size}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    <option value="" disabled>
                      Select practice size
                    </option>
                    {practiceSizes.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Complete Step */}
            {currentStep === 5 && (
              <div className="text-center">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                  {isSubmitting ? (
                    <Loader2 size={48} className="text-blue-600 animate-spin" />
                  ) : (
                    <Check size={48} className="text-green-600" />
                  )}
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {isSubmitting
                    ? "Saving Your Profile..."
                    : apiResponse && apiResponse.success
                    ? "Profile Saved Successfully!"
                    : "Setup Complete!"}
                </h2>

                {apiResponse && !apiResponse.success && (
                  <div className="mb-6 p-4 rounded-md bg-red-50 border border-red-200 text-red-700 mx-auto max-w-md">
                    <h3 className="font-semibold mb-1">Error</h3>
                    <p className="text-sm">
                      {apiResponse.message || "Failed to save profile data."}
                    </p>
                  </div>
                )}

                <p className="text-lg text-gray-700 mb-8">
                  {isSubmitting
                    ? "Please wait while we save your information..."
                    : apiResponse && apiResponse.success
                    ? "You're all set! You'll be redirected to your dashboard in a moment."
                    : "You're all set! Now let's start using DocuMD to save time on your documentation."}
                </p>

                <div className="flex justify-center">
                  {!isSubmitting && !apiResponse?.success && (
                    <button
                      onClick={handleSubmitToBackend}
                      className="px-8 py-3 font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                      disabled={isSubmitting}
                    >
                      Get Started
                      <ChevronRight size={18} />
                    </button>
                  )}

                  {apiResponse?.success && (
                    <div className="flex items-center justify-center space-x-2 text-green-600">
                      <CheckCircle size={20} />
                      <span>Redirecting to dashboard...</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            {currentStep !== 5 && (
              <div className="flex justify-between mt-10">
                <button
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-200 ${
                    currentStep === 0
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <ChevronLeft size={18} />
                  Back
                </button>
                <button
                  onClick={handleNext}
                  disabled={!isStepValid()}
                  className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-200 ${
                    !isStepValid()
                      ? "bg-blue-300 text-white cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Next
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DoctorOnboarding;
